/* eslint-disable react/prop-types */
/**
 * EntitiesTab — Buyer / Seller rows attached to a deal (deal_entities).
 *
 * - Splits the rows into BUYER (top) + SELLER (bottom) sub-sections so the
 *   user can sanity-check each side independently. The backend ensures the
 *   pct_deal_receiving column sums to 1.0 per side; if it doesn't, a
 *   POST/PUT response header `X-Deal-Entities-Pct-Warning` carries the
 *   current sum (e.g. "sum=0.95"). We surface that as an AntD warning
 *   notification — the row is still saved either way (app-layer warning,
 *   not a server reject).
 * - Lookup field `entity_id` uses the async `entities` typeahead.
 * - Create-on-the-fly (UAT #9): the `entities` table had no create path, so a
 *   Buyer/Seller couldn't be picked until it already existed. As of
 *   wig-react-library 1.0.101 the lookup Select supports inline creation via
 *   the `lookupCreators` prop — keyed by lookup name, each entry is
 *   `(inputLabel) => Promise<{id, label}>`. Typing a new name into the Entity
 *   picker surfaces a "+ Add '<name>'" affordance rendered by the library,
 *   which creates + selects the entity without leaving the form. This replaces
 *   the earlier stopgap header "Add Entity" modal.
 */
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Card, Space, notification } from "antd";

import { WigMetadataCrudPanel } from "@warwick-investment-group/wig-react-library";

import { AppContext } from "../../../AppContextProvider";
import usePermissions from "../../../hooks/usePermissions";
import dealsService from "../../../services/dealsService";
import { apiErrorText } from "../../../utils/apiError";
import {
  PCT_FIELD_RENDERERS,
  withShareBoundsNotice,
} from "../../../components/interestInput";
import { overAllocationError } from "../../../utils/interestBounds";

// What to do about a 409 name clash on inline create (#244).
const ENTITY_CLASH_HINT =
  "Pick it from the Entity list if it appears there, or ask the land team to add it to this deal.";

const SIDE_DEFS = [
  { type: "BUYER", title: "Buyers" },
  { type: "SELLER", title: "Sellers" },
];

// Fields the parent context controls — strip from metadata before passing
// to WigMetadataCrudPanel so they don't appear in the grid or the form.
// (The library panel has no `hideFields` prop, so pre-filtering metadata is
// the supported way to suppress columns.)
const HIDDEN_FIELDS = new Set(["deal_id", "entity_type"]);

// Fields the update endpoint accepts (DealEntityUpdateRequest, extra="forbid").
// We whitelist these on update rather than spreading the whole row: an existing
// entity carries server-managed fields (id, deal_id, created_*, updated_*) that
// the endpoint rejects with 422 — the "add works but edit fails" bug (UAT #26).
// A brand-new row has none of those, which is why create was unaffected.
const UPDATABLE_ENTITY_FIELDS = [
  "entity_id",
  "pct_deal_receiving",
  "contact_name",
  "contact_phone",
  "contact_email",
  "contact_street_address",
  "contact_city",
  "contact_zip",
  "contact_state",
];

// `pct_deal_receiving` is one DB column but reads differently per side: for a
// Buyer it's the share of the deal they RECEIVE; for a Seller it's the share
// they CONTRIBUTE. Relabel per side so the grid header + form field read
// naturally (UAT #8). The field stays visible on both sides.
const PCT_FIELD = "pct_deal_receiving";
const PCT_LABEL_BY_SIDE = {
  BUYER: "% Deal Receiving",
  SELLER: "% Deal Contributing",
};

// Pull "sum=0.95" -> 0.95 out of the warning header. Tolerate the
// alternative form where the entire header is a bare number.
const parsePctWarningHeader = (raw) => {
  if (!raw) return null;
  const m = String(raw).match(/sum\s*=\s*([\d.]+)/i);
  if (m) {
    const n = Number(m[1]);
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

// `pct_deal_receiving` is stored as a 0–1 decimal share, but land users think
// in percent. We let them enter/see 0–100 and convert at the tab boundary:
// ×100 for display, ÷100 on save. Rounding kills float noise (the stored
// column is numeric(10,8), i.e. up to 6 decimal places of percent).
const toPercent = (frac) =>
  frac == null || frac === "" ? frac : Number((Number(frac) * 100).toFixed(6));
const toFraction = (pct) =>
  pct == null || pct === "" ? undefined : Number((Number(pct) / 100).toFixed(8));

// Tolerance for the per-side "should total 100%" check (UAT #34/#35). The pct
// column is stored as a 0–1 fraction (numeric(10,8)), so compare the summed
// fractions against 1 with a small epsilon to absorb float noise.
const PCT_SUM_TOLERANCE = 1e-4;

// `onDealDerivedChange` — called after any seller/buyer write, so the parent
// re-fetches the deal row and the header card's Seller list (derived from these
// rows, feedback #70) stops showing the pre-edit names. Same contract as
// LeaseDeedsTab. Optional: without it the header lags until a page reload.
const EntitiesTab = ({ dealId, onDealDerivedChange }) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  // Create and edit differ on the server, so they differ here. POST
  // /deal_entities is the WIDER list — it includes BD.LeaseRecords /
  // BD.DivisionOrders, because attaching a buyer/seller is part of the
  // post-close workflow (UAT #9) — while PUT/DELETE are the narrower
  // [BrokerAgent, Landman, LandTech]. Collapsing them to one gate would
  // silently drop create-on-the-fly for the two post-close roles.
  const { can } = usePermissions();
  const mayAddEntity = can("dealEntity.create");
  const mayEditRecords = can("record.edit");

  const [rows, setRows] = useState([]);
  const [metadata, setMetadata] = useState([]);
  const [entityNames, setEntityNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState(null);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  const refresh = useCallback(async () => {
    if (!dealId) return;
    try {
      setLoading(true);
      setErrMsg(null);
      const { token: tk, userOid } = await getAuth();
      const [listResp, metaResp, entResp] = await Promise.all([
        dealsService.listDealEntities({ token: tk, userOid, dealId }),
        dealsService.getMetadata({ token: tk, userOid, tableName: "deal_entities" }),
        // Eager-load every entity (incl. inactive) so the grid can resolve the
        // `entity_id` Lookup column to a name instead of showing the raw id
        // (UAT #33). Mirrors the id→name map pattern in PaymentsTab.
        dealsService.listAllEntities({ token: tk, userOid, includeInactive: true }),
      ]);
      setRows(listResp?.data?.data || []);
      setMetadata(metaResp?.data?.data || []);
      const entRows = entResp?.data?.data || entResp?.data || [];
      const nameMap = {};
      for (const e of Array.isArray(entRows) ? entRows : []) {
        if (e && e.id != null) {
          nameMap[e.id] = e.name || e.display_name || `Entity ${e.id}`;
        }
      }
      setEntityNames(nameMap);
    } catch (err) {
      console.error("EntitiesTab load failed:", err);
      setErrMsg(apiErrorText(err));
    } finally {
      setLoading(false);
    }
  }, [dealId, getAuth]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filteredMetadata = useMemo(
    () => (metadata || []).filter((m) => !HIDDEN_FIELDS.has(m.field)),
    [metadata]
  );

  // Wire the `entities` async typeahead used by the `entity_id` Lookup
  // field. The library's `lookupFetchers[name]` contract is
  // `(query) => Promise<{id, label}[]>` — adapt the axios-shaped service
  // response to that shape. (The deleted in-app `MetadataFormFields.jsx`
  // hardcoded this adapter; the library expects the consumer to supply it.)
  const lookupFetchers = useMemo(
    () => ({
      entities: async (query) => {
        const { token: tk, userOid } = await getAuth();
        const resp = await dealsService.searchEntities({
          token: tk,
          userOid,
          query,
        });
        const rows = resp?.data?.data || resp?.data || [];
        return Array.isArray(rows)
          ? rows.map((r) => ({
              id: r.id,
              label: r.name || r.display_name || `Entity ${r.id}`,
            }))
          : [];
      },
    }),
    [getAuth]
  );

  // Inline create-on-the-fly for the `entities` lookup (wig-react-library
  // 1.0.101+). Keyed by lookup name; the library invokes this with the text
  // the user typed and expects `Promise<{id, label}>` for the freshly created
  // option, which it then selects. Mirrors `lookupFetchers` shape/normalization
  // but for POST instead of search. Backend is idempotent on normalized name,
  // so `data.name` is the authoritative label (fall back to the typed name).
  const lookupCreators = useMemo(
    () => ({
      entities: async (name) => {
        const { token: tk, userOid } = await getAuth();
        let resp;
        try {
          resp = await dealsService.createEntity({ token: tk, userOid, name });
        } catch (err) {
          // 409: for an external broker, de-hub only matches entities they can
          // already see; a name that exists elsewhere is a plain 409 rather
          // than the existing row. Retrying cannot help, so say what to do
          // instead — ON the Entity field ({fieldError}, wig-react-library
          // 1.0.130+), where the dropdown stays open with nothing selected
          // and the message clears as soon as they type or pick. Anything
          // else still rejects, and the library shows its generic toast.
          if (err?.response?.status !== 409) throw err;
          const detail = err?.response?.data?.detail;
          const what =
            typeof detail === "string" && detail
              ? detail
              : "An entity with this name already exists.";
          return {
            fieldError: `${what} ${ENTITY_CLASH_HINT}`,
          };
        }
        const c = resp?.data?.data || resp?.data || {};
        return { id: c.id, label: c.name || name };
      },
    }),
    [getAuth]
  );

  // Grid cell renderers. The library passes a `{ value, data, index }` params
  // object (NOT the bare value) — destructure `value` accordingly (UAT #33: the
  // old bare-arg version rendered "[object Object]%").
  // - pct column: show the (already percent-scaled) value with a trailing "%".
  // - entity_id Lookup: resolve the id to the entity name via `entityNames`.
  const cellRenderers = useMemo(
    () => ({
      [PCT_FIELD]: ({ value }) =>
        value == null || value === "" ? "" : `${value}%`,
      entity_id: ({ value }) =>
        value == null || value === "" ? "" : entityNames[value] ?? value,
    }),
    [entityNames]
  );

  const surfacePctWarning = (resp, side) => {
    const header =
      resp?.headers?.["x-deal-entities-pct-warning"] ||
      resp?.headers?.["X-Deal-Entities-Pct-Warning"];
    const sum = parsePctWarningHeader(header);
    if (sum == null) return;
    notification.warning({
      message: `${side === "BUYER" ? "Buyer" : "Seller"} percentages don't total 100% yet`,
      description: `Current total: ${toPercent(sum)}%`,
      duration: 6,
    });
  };

  // Over-allocation guard (the blocking half of the "should total 100%" rule).
  // Summed here rather than in the edit form because the form has no way to
  // know which row it is editing — `id` is server-managed, so the panel filters
  // it out of the form values. Throwing keeps the panel's modal open and
  // surfaces the message in its "Save failed" dialog (see WigMetadataCrudPanel
  // onSubmit), so the row is never sent.
  //
  // Only OVER-allocation blocks. Under-allocation is a normal intermediate
  // state — you can't reach 100% while entering the first buyer — so it stays
  // the warning banner below.
  const assertWithinAllocation = (side, pct, excludeId) => {
    const othersTotal = rows
      .filter(
        (r) => r.entity_type === side && (excludeId == null || r.id !== excludeId)
      )
      .reduce((acc, r) => acc + toPercent(Number(r.pct_deal_receiving) || 0), 0);
    const err = overAllocationError({
      othersTotal,
      value: pct,
      sideTitle: side === "BUYER" ? "Buyers" : "Sellers",
      label: PCT_LABEL_BY_SIDE[side],
    });
    if (err) throw new Error(err);
  };

  const onCreateFor = (side) => async (body) => {
    assertWithinAllocation(side, body.pct_deal_receiving, null);
    const { token: tk, userOid } = await getAuth();
    const resp = await dealsService.createDealEntity({
      token: tk,
      userOid,
      body: {
        ...body,
        pct_deal_receiving: toFraction(body.pct_deal_receiving),
        deal_id: dealId,
        entity_type: side,
      },
    });
    surfacePctWarning(resp, side);
    await refresh();
    await onDealDerivedChange?.();
    return resp;
  };

  const onUpdate = (side) => async (id, body) => {
    assertWithinAllocation(side, body.pct_deal_receiving, id);
    const { token: tk, userOid } = await getAuth();
    // Send ONLY the fields the update endpoint accepts (UAT #26). Spreading the
    // whole edited row leaks server-managed fields (id, deal_id, created_*,
    // updated_*) that DealEntityUpdateRequest forbids -> 422 on every edit.
    const clean = {};
    for (const k of UPDATABLE_ENTITY_FIELDS) {
      if (body[k] !== undefined) clean[k] = body[k];
    }
    clean.entity_type = side;
    clean.pct_deal_receiving = toFraction(body.pct_deal_receiving);
    const resp = await dealsService.updateDealEntity({
      token: tk,
      userOid,
      id,
      body: clean,
    });
    surfacePctWarning(resp, side);
    await refresh();
    await onDealDerivedChange?.();
    return resp;
  };

  const onDelete = async (id) => {
    const { token: tk, userOid } = await getAuth();
    await dealsService.deleteDealEntity({ token: tk, userOid, id });
    await refresh();
    await onDealDerivedChange?.();
  };

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }} className="wig-multi-table-tab">
      {errMsg && <Alert type="error" showIcon message="Failed to load entities" description={errMsg} />}

      {SIDE_DEFS.map(({ type, title }) => {
        const sideRows = rows.filter((r) => r.entity_type === type);
        // UAT #34/#35: warn when a side's percentages don't total 100%. Sum the
        // stored 0–1 fractions and compare to 1 within tolerance.
        const pctSum = sideRows.reduce(
          (acc, r) => acc + (Number(r.pct_deal_receiving) || 0),
          0
        );
        const pctOff =
          sideRows.length > 0 && Math.abs(pctSum - 1) > PCT_SUM_TOLERANCE;
        // Present the pct column as a percent (stored as a 0–1 share).
        const displayRows = sideRows.map((r) =>
          r.pct_deal_receiving == null
            ? r
            : { ...r, pct_deal_receiving: toPercent(r.pct_deal_receiving) }
        );
        // Relabel the pct column per side (receiving for buyers, contributing
        // for sellers) and replace its jargon hint with a plain percent one.
        const panelMetadata = filteredMetadata.map((m) =>
          m.field === PCT_FIELD
            ? {
                ...m,
                label: PCT_LABEL_BY_SIDE[type] || m.label,
                definition: `Percent of the deal this ${
                  type === "BUYER" ? "buyer receives" : "seller contributes"
                } — enter 0–100 (e.g. 60 for 60%). Each side should total 100%.`,
              }
            : m
        );
        // Full-width banner at the top of the pct field's section in the
        // Add/Edit modal, plus the 0–100 per-row guard.
        const panelProps = withShareBoundsNotice({
          metadata: panelMetadata,
          customRenderers: PCT_FIELD_RENDERERS,
        });
        return (
          <Card key={type} size="small" title={`${title} (${sideRows.length})`}>
            {pctOff && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={`${title} ${PCT_LABEL_BY_SIDE[type]} totals ${toPercent(
                  pctSum
                )}% — should total 100%.`}
              />
            )}
            <WigMetadataCrudPanel
              title={title}
              rows={displayRows}
              loading={loading}
              allowView
              disableNonEditable
              onCreate={mayAddEntity ? onCreateFor(type) : undefined}
              onUpdate={mayEditRecords ? onUpdate(type) : undefined}
              onDelete={mayEditRecords ? onDelete : undefined}
              lookupFetchers={lookupFetchers}
              lookupCreators={lookupCreators}
              cellRenderers={cellRenderers}
              {...panelProps}
              newRowDefaults={{ deal_id: dealId, entity_type: type }}
              emptyText={`No ${title.toLowerCase()} on this deal yet`}
              editTrigger="button"
              editLayout="wide"
              height={260}
            />
          </Card>
        );
      })}
    </Space>
  );
};

export default EntitiesTab;
